import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { OnInit, AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, HostBinding, Input, OnDestroy, Optional, Output, Self, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NgControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { FocusMonitor } from '@angular/cdk/a11y';
import { QuestionModel } from '../../question.service';
import { Field } from '../../process.Service';
import { QuillEditorComponent } from 'ngx-quill';
import ImageUploader from './addons/quill-image-uploader';
import PipeSelector from './addons/PipeSelector';
import { MediaService } from '../../media.Service';
import { baseUrl } from '../../../environments/environment';
import FroalaEditor from 'froala-editor';
import { FieldSelectorComponent } from '../../data-analysis/custom-format/field-selector/field-selector.component';

@Component({
  selector: 'diy-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.css'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => EditorComponent) }]
})
export class EditorComponent implements OnInit, ControlValueAccessor, AfterViewInit, OnDestroy {
  static nextId = 0;

  options: Object = {
    key: 'yDC5hB3E3B11A8E5F6E4g1JWSDBCQG1ZGDf1C1d2JXDAAOZWJhE5B4E4I3F2H3A7A4E5B4==',
    charCounterCount: true,
    attribution: false,
    imageUpload: true,
    imageUploadURL: baseUrl + 'Media/UploadForFroala',
    toolbarButtons: [['paragraphFormat'], ['bold', 'italic', 'underline', 'strikeThrough'], ['textColor', 'backgroundColor'], ['formatOL', 'formatUL'], ['insertLink', 'insertImage', 'pipe'],['html', 'fullscreen']],
    toolbarButtonsXS: ['bold', 'italic', 'underline', 'strikeThrough', 'paragraphFormat', 'html', 'pipe', 'insertImage'],
    toolbarButtonsSM: ['bold', 'italic', 'underline', 'strikeThrough', 'paragraphFormat', 'html', 'pipe', 'insertImage'],
    toolbarButtonsMD: ['bold', 'italic', 'underline', 'strikeThrough', 'paragraphFormat', 'html', 'pipe', 'insertImage'],
    codeViewKeepActiveButtons: ['pipe']
  };

  formGroup: FormGroup;
  controlType = 'diy-editor';

  @ViewChild('selector') public selector: FieldSelectorComponent | undefined;

  mouseX: number = 0;
  mouseY: number = 0;

  constructor(fb: FormBuilder, private fm: FocusMonitor, private elRef: ElementRef, public mediaService: MediaService
  ) {
    this.formGroup = fb.group({ 'value': '' })
    this.formGroup.valueChanges.subscribe(x => {
      this._value = this.formGroup.controls.value.value;
      if (this.onChange) {
        this.onChange(this._value);
      }
    });
  }

  onMouseMove(event: MouseEvent): void {
    this.mouseX = event.clientX;
    this.mouseY = event.clientY;
  };

  ngOnInit(): void {
    FroalaEditor.DefineIconTemplate('material_design', '<i class="zmdi zmdi-[NAME]"></i>');
    FroalaEditor.DefineIcon('pipe', { NAME: 'colorize', template: 'material_design' });
    FroalaEditor.RegisterCommand('pipe', {
      title: 'Pipe',
      icon: 'pipe',
      focus: false,
      undo: false,
      refreshAfterCallback: false,

      callback: () => {
        this.selector.onContextMenu(new MouseEvent("click", {
          clientX: this.mouseX,
          clientY: this.mouseY,
          bubbles: true,
          cancelable: true
        }));
      }
    });
  }

  ngAfterViewInit(): void {
  }

  initControls: any;
  edit: any;
  initialize(initControls) {
    this.initControls = initControls;
    this.initControls.initialize();
  }

  selectField(field: Field) {
    this.edit = this.initControls.getEditor();
    let selection = this.edit.selection.get();
    this.edit.html.insert("{" + field.Name + "}", false);
    this.edit.save.force();

    /*
    if (this.editor == undefined) {
      return;
    }

    let selection = this.editor.quillEditor.getSelection();
    let fieldString = "{" + field.Name + "}";
    this.editor.quillEditor.insertText(selection.index, fieldString);
    this.formGroup.controls.value.setValue(this.editor.quillEditor.root.innerHTML);*/
  }

  writeValue(obj: any): void {
    this.value = obj;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

  @Output()
  onSmart: EventEmitter<string> = new EventEmitter<string>();

  clickSmart(value: string) {
    if (this.onSmart) {
      this.onSmart.emit(value);
    }
  }

  onChange = (value: any) => { };

  stateChanges = new Subject<void>();

  touched = false;

  onTouched() {

  }

  get smsText(): string {
    return this._smsText;
  }
  set smsText(value: string) {
    this._smsText = value;
    this.onChange(this._smsText);
  }
  private _smsText: string = "";

  smsChanged($event: any) {
    this.smsText = $event;
  }

  @HostBinding() id = `diy-editor-${EditorComponent.nextId++}`;

  describedBy = '';

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  @Input()
  get questions(): QuestionModel[] {
    return this._questions;
  }
  set questions(value: QuestionModel[]) {
    this._questions = value;
  }
  private _questions: QuestionModel[] = [];

  @Input()
  get disabled() { return this._disabled; }
  set disabled(dis) {
    this._disabled = coerceBooleanProperty(dis);
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get height(): number {
    return this._height;
  }
  set height(value: number) {
    this._height = value;
    (this.options as any).height = value;
    this.stateChanges.next();
  }
  private _height: number = 0;

  @Input()
  get value(): string | null {
    switch (this._channel) {
      case "sms":
      case "whatsapp":
        this._value = this.smsText;
        break;
      case "web":
        this._value = this.formGroup.controls.value.value;
        break;
    }

    return this._value;
  }
  set value(v: string | null) {
    this._smsText = this._value = v ?? "";
    this.formGroup.controls.value.setValue(v);
    this.stateChanges.next();
  }
  private _value: string = "";

  @Input()
  get channel(): string {
    return this._channel;
  }
  set channel(value: string) {
    this._channel = value;
  }
  private _channel = "web";

  @Input()
  get smart(): boolean {
    return this._smart;
  }
  set smart(value: boolean) {
    this._smart = value;
  }
  private _smart: boolean = false;
}
